import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Card } from '../components/ui/Card';
import toast from 'react-hot-toast';

export default function QuotesPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [syncingFrom4D, setSyncingFrom4D] = useState(false);
  const [syncingToZoneDM, setSyncingToZoneDM] = useState(false);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest'); // Default: newest first
  const [syncStatusFilter, setSyncStatusFilter] = useState<string>('all'); // Default: show all statuses

  const { data: quotes, isLoading } = useQuery({
    queryKey: ['quotes', sortOrder],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      // Supabase has a default limit of 1000, so we need to fetch all in batches
      let allQuotes: any[] = [];
      let from = 0;
      const batchSize = 1000;
      let hasMore = true;
      
      while (hasMore) {
      const { data, error } = await supabase
        .from('quotes')
        .select(`
          *,
          quote_procedure_mapping(
            procedure_external_id,
            procedures:procedures(
              external_id,
              name
            )
          )
        `)
        .eq('user_id', session.user.id)
        .order('modified_date', { ascending: sortOrder === 'oldest' })
        .range(from, from + batchSize - 1);
          
        if (error) throw error;
        
        if (data && data.length > 0) {
          allQuotes = [...allQuotes, ...data];
          from += batchSize;
          hasMore = data.length === batchSize;
        } else {
          hasMore = false;
        }
      }
      
      return allQuotes;
    },
  });

  // Exact DB totals (not limited by default 1000 row response)
  const { data: quotesTotalCount = 0, refetch: refetchTotalCount } = useQuery({
    queryKey: ['quotes-total-count'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { count, error } = await supabase
        .from('quotes')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', session.user.id);
      if (error) throw error;
      return count || 0;
    },
    staleTime: 0, // Always consider stale to allow refetching
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });
  const { data: quotesPendingCount = 0, refetch: refetchPendingCount } = useQuery({
    queryKey: ['quotes-pending-count'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { count, error } = await supabase
        .from('quotes')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', session.user.id)
        .in('zonedm_sync_status', ['received','zonedm_error','queued']);
      if (error) throw error;
      return count || 0;
    },
    staleTime: 0, // Always consider stale to allow refetching
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  const filtered = useMemo(() => {
    let list = quotes || [];
    
    // Apply sync status filter
    if (syncStatusFilter !== 'all') {
      list = list.filter((q: any) => {
        const status = (q?.zonedm_sync_status ?? q?.sync_status ?? '') as string;
        if (syncStatusFilter === 'empty') {
          return !status || status === '';
        }
        return status === syncStatusFilter;
      });
    }
    
    // Apply search filter
    const term = searchTerm.trim().toLowerCase();
    if (!term) return list;
    return list.filter((q: any) => {
      const quoteNo = String(q?.price_quote_no ?? q?.price_quote_id ?? '').toLowerCase();
      const patient = String(q?.patient_name ?? q?.patient_external_id ?? '').toLowerCase();
      const status = String(q?.status_name ?? '').toLowerCase();
      const total = String(q?.total_amount ?? '').toLowerCase();
      const procedure = String(q?.procedure_description ?? '').toLowerCase();
      const syncStatus = String(q?.zonedm_sync_status || q?.sync_status || '').toLowerCase();
      return (
        quoteNo.includes(term) || patient.includes(term) || status.includes(term) || total.includes(term) || procedure.includes(term) || syncStatus.includes(term)
      );
    });
  }, [quotes, searchTerm, syncStatusFilter]);

  // Seed search from query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get('search');
    if (s) setSearchTerm(s);
  }, []);

  const total = filtered.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const paginated = useMemo(() => filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage), [filtered, page, rowsPerPage]);

  const syncFrom4D = async () => {
    setSyncingFrom4D(true);
    try {
      // Tokens are refreshed per-request below
      let logId: string | undefined = undefined;
      let isLastPage = false;
      let pageIdx = 0;
      let totalEstimate: number | undefined;
      let processed = 0;
      const toastId = 'quotes-sync';
      toast.loading('Syncing quotes (0/?) …', { id: toastId });
      while (!isLastPage) {
        // Fetch one page from 4D and stage it
        const sFetch = await supabase.auth.getSession();
        const accessTokenFetch = sFetch.data.session?.access_token;
        const fetchRes: Response = await fetch(`${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/quotes-fetch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessTokenFetch}` },
          body: JSON.stringify({ logId }),
        });
        const fetchJson: any = await fetchRes.json();
        if (!fetchRes.ok) throw new Error(fetchJson?.error || 'Fetch failed');
        logId = fetchJson?.log_id || logId;
        isLastPage = !!fetchJson?.isLastPage;
        pageIdx += 1;
        totalEstimate = typeof fetchJson?.totalEstimate === 'number' ? fetchJson.totalEstimate : totalEstimate;

        // Process that staged batch
        const sProc = await supabase.auth.getSession();
        const accessTokenProc = sProc.data.session?.access_token;
        const processRes: Response = await fetch(`${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/quotes-process`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessTokenProc}` },
          body: JSON.stringify({ logId, batchId: fetchJson?.batchId }),
        });
        const processJson: any = await processRes.json();
        if (!processRes.ok) throw new Error(processJson?.error || 'Process failed');
        processed += Number(processJson?.processed || 0);
        const totalStr = totalEstimate != null ? String(totalEstimate) : '?';
        toast.loading(`Syncing quotes (${processed}/${totalStr}) …`, { id: toastId });
      }
      toast.success(`Sync FROM 4D completed (${processed}${totalEstimate != null ? '/' + totalEstimate : ''}).`, { id: 'quotes-sync' });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quotes-total-count'] });
      queryClient.invalidateQueries({ queryKey: ['quotes-pending-count'] });
      // Force refetch to update UI immediately
      await Promise.all([
        refetchTotalCount(),
        refetchPendingCount(),
      ]);
    } catch (err: any) {
      toast.error('Sync FROM 4D failed: ' + (err.message || err.error_description || 'Unknown error'), { id: 'quotes-sync' });
    } finally {
      setSyncingFrom4D(false);
    }
  };

  const syncToZoneDM = async () => {
    setSyncingToZoneDM(true);
    const toastId = 'quotes-push';
    toast.loading('Pushing quotes to ZoneDM…', { id: toastId });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const userId = session.user.id;
      const batchSize = 10;
      // Compute total items to push once for consistent progress
      const { count: toPushTotal } = await supabase
        .from('quotes')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .in('zonedm_sync_status', ['received','zonedm_error']);
      let totalPushed = 0;
      let lastErrorBatchSize = 0; // Track size of last error-only batch to detect retries
      // Loop: prepare a batch (Supabase) -> process that batch (Netlify)
      while (true) {
        // Prepare on Supabase
        const fresh = await supabase.auth.getSession();
        const accessToken = fresh.data.session?.access_token;
        const prep = await fetch(`${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/quotes-push-prepare`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
          body: JSON.stringify({ batchSize }),
        });
        const prepJson: any = await prep.json();
        if (!prep.ok) throw new Error(prepJson?.error || 'Prepare batch failed');
        const batchId: string | null = prepJson?.batchId || null;
        const itemCount: number = prepJson?.itemCount || 0;
        if (!batchId || itemCount === 0) break; // nothing left

        // Process on Netlify
        const url = `/.netlify/functions/manual-push-quotes?user_id=${encodeURIComponent(userId)}&batch_id=${encodeURIComponent(batchId)}`;
        const res: Response = await fetch(url);
        const body: any = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.error || 'Push failed');
        const batchSuccess = Number(body?.success || 0);
        const batchErrors = Number(body?.errors || 0);
        totalPushed += batchSuccess;

        // Check if we've processed everything - get current remaining count
        const { count: remainingCount } = await supabase
          .from('quotes')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .in('zonedm_sync_status', ['received','zonedm_error']);
        
        // If remaining is 0, we're done
        if ((remainingCount ?? 0) === 0) {
          break;
        }
        
        // Detect stuck state: all items failed, it's a small batch (end of queue), 
        // and we're seeing the same error batch size repeatedly
        const allFailed = batchSuccess === 0 && batchErrors > 0;
        const isSmallBatch = itemCount < batchSize;
        if (allFailed && isSmallBatch && itemCount === lastErrorBatchSize && lastErrorBatchSize > 0) {
          // We're stuck retrying the same error items - break to prevent infinite loop
          toast.loading(`Pushing quotes… (+${batchSuccess}, errors ${batchErrors}, ${totalPushed}/${toPushTotal}) - Stopping: persistent errors detected`, { id: toastId });
          break;
        }
        if (allFailed) {
          lastErrorBatchSize = itemCount; // Remember this error batch size
        } else {
          lastErrorBatchSize = 0; // Reset on success
        }
        
        const totalStr = typeof toPushTotal === 'number' ? `${totalPushed}/${toPushTotal}` : `${totalPushed}`;
        toast.loading(`Pushing quotes… (+${batchSuccess}, errors ${batchErrors}, ${totalStr})`, { id: toastId });
        // Refresh table incrementally
        queryClient.invalidateQueries({ queryKey: ['quotes'] });
      }
      const doneStr = typeof toPushTotal === 'number' ? `${totalPushed}/${toPushTotal}` : String(totalPushed);
      toast.success(`Sync TO ZoneDM completed (${doneStr}).`, { id: toastId });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
    } catch (err: any) {
      toast.error('Sync TO ZoneDM failed: ' + (err.message || 'Unknown error'), { id: toastId });
    } finally {
      setSyncingToZoneDM(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Quotes</h1>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">Total: {quotesTotalCount} • Pending: {quotesPendingCount}</div>
          <button onClick={syncFrom4D} className="bg-blue-600 text-white px-4 py-2 rounded flex items-center justify-center min-w-[120px]" disabled={syncingFrom4D}>
            {syncingFrom4D ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                Syncing...
              </>
            ) : (
              'Sync FROM 4D'
            )}
          </button>
          <button onClick={syncToZoneDM} className="bg-green-600 text-white px-4 py-2 rounded flex items-center justify-center min-w-[140px]" disabled={syncingToZoneDM}>
            {syncingToZoneDM ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                Syncing...
              </>
            ) : (
              'Sync TO ZoneDM'
            )}
          </button>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700 font-medium">Search:</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              placeholder="Quote #, patient, status, amount, procedure"
              className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white min-w-[260px]"
            />
          </div>
        </div>
      </div>
      <Card>
        <div className="overflow-x-auto p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-gray-50 border border-gray-200 rounded-md px-4 py-3 mb-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 font-medium">Rows per page:</label>
              <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }} className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white">
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={500}>500</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 font-medium">Sort:</label>
              <select
                value={sortOrder}
                onChange={(e) => { setSortOrder(e.target.value as 'newest' | 'oldest'); setPage(1); }}
                className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 font-medium">ZoneDM Status:</label>
              <select
                value={syncStatusFilter}
                onChange={(e) => { setSyncStatusFilter(e.target.value); setPage(1); }}
                className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white"
              >
                <option value="all">All Statuses</option>
                <option value="received">Received</option>
                <option value="zonedm_synced">Synced</option>
                <option value="zonedm_error">Error</option>
                <option value="missing_contact">Missing Contact</option>
                <option value="empty">Empty</option>
              </select>
            </div>
            <div className="text-sm text-gray-500 font-medium">Page {page} of {totalPages}</div>
          </div>

          <table className="min-w-full divide-y divide-gray-200 text-xs shadow-sm border border-gray-200 rounded-md bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quote #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Procedures</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appt Start</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modified</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ZoneDM Sync</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(!paginated || paginated.length === 0) && (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-gray-400">{isLoading ? 'Loading...' : 'No quotes found.'}</td>
                </tr>
              )}
              {paginated.map((q: any) => (
                <tr key={q.id} className="border-t hover:bg-gray-50">
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-indigo-700 underline">
                    {(q.price_quote_no ?? q.price_quote_id) ? (
                      <Link to={`/dashboard/quotes?search=${encodeURIComponent(String(q.price_quote_no ?? q.price_quote_id))}`}>
                        {q.price_quote_no ?? q.price_quote_id}
                      </Link>
                    ) : '—'}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-indigo-700 underline">
                    {(q.patient_name || q.patient_external_id) ? (
                      <Link to={`/dashboard/patients?search=${encodeURIComponent(String(q.patient_name ?? q.patient_external_id))}`}>
                        {q.patient_name ?? q.patient_external_id}
                      </Link>
                    ) : '—'}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">{q.status_name ?? '—'}</td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">{q.total_amount != null ? `$${Number(q.total_amount).toFixed(2)}` : '—'}</td>
                  <td className="px-6 py-3 text-sm text-gray-900">
                    {(() => {
                      // Get procedures from quote_procedure_mapping join
                      const procedures = (q.quote_procedure_mapping as any[]) || [];
                      if (procedures.length === 0) {
                        // Fallback to procedure_description if no mappings yet
                        return q.procedure_description ? (
                          <span className="text-gray-500 italic" title="Procedure description (mapping not yet synced)">
                            {q.procedure_description}
                          </span>
                        ) : '—';
                      }
                      return (
                        <div className="flex flex-col gap-1">
                          {procedures.map((proc, idx) => {
                            const procData = proc.procedures;
                            return (
                              <div key={idx} className="flex flex-col">
                                <span className="text-gray-900 font-medium">
                                  {procData?.name || 'Unknown'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  ID: {proc.procedure_external_id}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-900">
                    {(() => {
                      // Extract providers from the providers array stored in the quote
                      const providersArray = (q.providers as Array<{ Id?: number; Name?: string }>) || [];
                      
                      if (providersArray.length === 0) {
                        return '—';
                      }
                      
                      // Extract unique provider names (deduplicate by Id)
                      const providerMap = new Map<number, string>();
                      providersArray.forEach((provider: { Id?: number; Name?: string }) => {
                        if (provider.Id && provider.Name) {
                          providerMap.set(provider.Id, provider.Name);
                        }
                      });
                      
                      const providerNames = Array.from(providerMap.values());
                      
                      if (providerNames.length === 0) {
                        return '—';
                      }
                      
                      return (
                        <div className="flex flex-col gap-1">
                          {providerNames.map((name, idx) => (
                            <span key={idx} className="text-gray-900">
                              {name}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-indigo-700 underline">
                    {q.appointment_external_id ? (
                      <Link to={`/dashboard/appointments?search=${encodeURIComponent(String(q.appointment_external_id))}`}>
                        {q.appointment_start ? new Date(q.appointment_start).toLocaleString() : q.appointment_external_id}
                      </Link>
                    ) : (q.appointment_start ? new Date(q.appointment_start).toLocaleString() : '—')}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">{q.modified_date ? new Date(q.modified_date).toLocaleString() : '—'}</td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm">
                    {(() => {
                      const status = (q.zonedm_sync_status ?? q.sync_status) as string | undefined;
                      const errorMsg = (q.zonedm_sync_error as string | undefined) || undefined;
                      const base = 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full';
                      const cls = !status || status === 'received'
                        ? 'bg-yellow-100 text-yellow-800'
                        : status === 'zonedm_synced' || status === 'synced'
                        ? 'bg-green-100 text-green-800'
                        : status === 'zonedm_error'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800';
                      const pill = (
                        <span className={`${base} ${cls}`}>{status || 'received'}</span>
                      );
                      if (status === 'zonedm_error' && errorMsg) {
                        return (
                          <button
                            type="button"
                            className="underline decoration-dotted decoration-red-400"
                            onClick={(e) => { e.stopPropagation(); /* toast imported at top? not here; stay non-toast */ alert(errorMsg); }}
                            title={errorMsg}
                          >
                            {pill}
                          </button>
                        );
                      }
                      return pill;
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-6">
            <button className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</button>
            <div className="text-sm text-gray-600">{(page - 1) * rowsPerPage + 1} - {Math.min(page * rowsPerPage, total)} of {total}</div>
            <button className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</button>
          </div>
        </div>
      </Card>
    </div>
  );
}


